import {
    HttpTransportType,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
} from "@microsoft/signalr";
import { sessionEventTypes } from "../constants/constants";
import storage from "../hooks/storage";

// Singleton wrapper around the Cobrowse SignalR hub. WebSocket-only (no
// long-polling fallback) with @microsoft/signalr's built-in automatic
// reconnect. Instantiation is deferred — the module-level `export default new
// SignalR()` allocates the class, but no network connection is opened until a
// consumer (e.g. VisitorList) actually calls `Connect(token, ...)`. This
// keeps the ShowMe (Video Support) tab free of any socket traffic when
// Cobrowse mode isn't active.
class SignalR {
    constructor() {
        this.connection = null;
        this.groupName = "domain";
        this.sender = null;
        this.deviceInfo = null;
        this.replayEvent = null;
        this.deviceDisconnected = null;
        this.bufferEvents = [];
    }

    DefineConnection = (token) => {
        this.connection = new HubConnectionBuilder()
            .withUrl(process.env.REACT_APP_SOCKET_CONNECTION_URL, {
                accessTokenFactory: () => token,
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Information)
            .build();
    };

    CheckConnectionEstablished = () => {
        return this.connection.state === HubConnectionState.Connected;
    };

    CheckConnectionAndSendEvent = (token, eventToCall, callbackFun) => {
        if (!this.connection) {
            this.DefineConnection(token);
        }

        if (this.connection.state === HubConnectionState.Disconnected) {
            this.connection
                .start()
                .then(() => {
                    this.connection.on("SendMessage", (user, message) => {
                        this.ReceiveMessage(user, message);
                    });

                    eventToCall(callbackFun);
                })
                .catch((error) => {
                    console.error(error.message);
                });
        } else {
            eventToCall();
        }
    };

    Connect = (token, callbackFun) => {
        this.CheckConnectionAndSendEvent(
            token,
            () => this.ConnectingCallMessage(callbackFun || false),
            callbackFun || false
        );
    };

    DefineSender = (callback) => {
        if (!this.sender) this.sender = callback;
    };

    DefineDeviceInfo = (callback) => {
        if (!this.deviceInfo) this.deviceInfo = callback;
    };

    DefineReplayEvent = (callback) => {
        if (!this.replayEvent) {
            this.replayEvent = callback;
        }
    };

    DefineDeviceDisconnected = (callback) => {
        if (!this.deviceDisconnected) this.deviceDisconnected = callback;
    };

    ReceiveMessage = (user, message) => {
        const parsedMessage = message ? JSON.parse(message) : { event: null };
        if (parsedMessage?.event) {
            switch (parsedMessage?.event) {
                case sessionEventTypes.replaySession:
                    this?.sender(parsedMessage.data);
                    this.callTime = null;
                    break;
                case sessionEventTypes.deviceInfo:
                    this?.deviceInfo(parsedMessage.data);
                    console.warn(sessionEventTypes.deviceInfo, user, parsedMessage);
                    break;
                case sessionEventTypes.replayEvent:
                    if (this.replayEvent) {
                        if (this.bufferEvents.length > 0) {
                            this.bufferEvents.forEach((element) => {
                                this.replayEvent(element);
                            });
                            setTimeout(() => {
                                this.bufferEvents = [];
                            }, 100);
                        }
                        this.replayEvent(parsedMessage.data);
                    } else {
                        this.bufferEvents.push(parsedMessage.data);
                    }
                    console.warn(sessionEventTypes.replayEvent, user, parsedMessage);
                    break;
                default:
                    break;
            }
        } else if (parsedMessage?.eventType) {
            switch (parsedMessage?.eventType) {
                case sessionEventTypes.connected:
                    console.log(sessionEventTypes.connected, user, parsedMessage);
                    break;
                case sessionEventTypes.disconnected:
                    console.log(sessionEventTypes.disconnected, user, parsedMessage);
                    this?.deviceDisconnected(parsedMessage);
                    break;
                default:
                    break;
            }
        }
    };

    ConnectingCallMessage = (callbackFun) => {
        console.log("ConnectingCallMessage function call");
        if (callbackFun) callbackFun();
    };

    SendMessageToGroup = (_sender, message, groupName = null) => {
        if (this?.connection.state === HubConnectionState.Connected) {
            const ucid = storage.getUID();
            this.connection
                .invoke(
                    "SendMessageToGroup",
                    groupName.toString(),
                    ucid.toString(),
                    JSON.stringify(message)
                )
                .then(() => {
                    console.warn("Message sent to group", message);
                })
                .catch((error) => {
                    console.error(error.message);
                });
        }
    };

    JoinGroup = (GroupName) => {
        console.warn("GroupName-session Id => ", GroupName);
        if (this?.connection.state === HubConnectionState.Connected) {
            this.connection
                .invoke("JoinGroup", GroupName?.toString())
                .then(() => {
                    console.warn("Group join successfully");
                })
                .catch((error) => {
                    console.error(error.message);
                });
        }
    };
}

export default new SignalR();
